import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

const layoutFormSchema = z.object({
  isSidebarExpanded: z.boolean(),
});

type LayoutFormValues = z.infer<typeof layoutFormSchema>;

export const LayoutForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    isSidebarExpanded:
      settings['ribbonPlugin.settings']?.is_sidebar_expanded ?? true,
  };

  const form = useForm<LayoutFormValues>({
    resolver: zodResolver(layoutFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: LayoutFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        is_sidebar_expanded: data.isSidebarExpanded,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          title: messages.genericToastTitleSuccess,
          description: messages.settingsGeneralSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          title: messages.genericToastTitleError,
          description: messages.settingsGeneralFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Layout</h3>
            <div className="space-y-4">
              <FormField
                key="isSidebarExpanded"
                control={form.control}
                name="isSidebarExpanded"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {
                        messages.settingsGeneralLayoutSidebarExpandedLabel
                        }
                      </FormLabel>
                      <FormDescription>
                        {
                        messages.settingsGeneralLayoutSidebarExpandedDescription
                        }                        
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="sidebar-expanded-layout-switch"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-general-layout-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
